/**
 * Pure pipeline helpers — no React, no Firebase. Kept separate so the stepper
 * and template-selection rules can be reasoned about (and tested) on their own.
 */
import type { PipelineOutcome, PipelineStage, TemplateKey } from '@/types/crm'

export interface StepperNode {
  label: string
  bg: string
  border: string
  dot: string
  text: string
  /** False on the last node, which has no trailing arrow. */
  hasArrow: boolean
}

/**
 * Build the process stepper for a lead.
 *
 * Current stage renders in its own colour; passed stages get the light
 * "completed" blue tint; future stages stay neutral grey. A lead that has
 * reached an outcome shows that outcome specifically as the final node —
 * otherwise the final node is a neutral "Won / Lost" placeholder.
 */
export function buildSteps(
  stageKey: string,
  allStages: PipelineStage[],
  outcomes: PipelineOutcome[],
): StepperNode[] {
  // A hidden stage is a holding pen, not a step on the way to Won — showing
  // "Unverified → Prospect → …" on every lead would make it look like one.
  // It appears only on the leads actually sitting in it.
  const stages = allStages.filter((s) => !s.hidden || s.key === stageKey)
  const activeIdx = stages.findIndex((s) => s.key === stageKey)
  const isOutcome = activeIdx === -1

  const steps: StepperNode[] = stages.map((s, i) => {
    if (!isOutcome && i === activeIdx) {
      return { label: s.label, bg: s.bg, border: s.color, dot: s.color, text: s.color, hasArrow: true }
    }
    if (isOutcome || i < activeIdx) {
      return {
        label: s.label,
        bg: '#f8faff',
        border: '#d3dce8',
        dot: '#3456DA',
        text: '#4c5b82',
        hasArrow: true,
      }
    }
    return {
      label: s.label,
      bg: '#f1f2f3',
      border: '#e2e5e9',
      dot: '#AEB1B4',
      text: '#4c5b82',
      hasArrow: true,
    }
  })

  const reached = isOutcome ? outcomes.find((o) => o.key === stageKey) : undefined
  if (reached) {
    steps.push({
      label: reached.label,
      bg: reached.bg,
      border: reached.color,
      dot: reached.color,
      text: reached.color,
      hasArrow: false,
    })
  } else {
    steps.push({
      label: outcomes.map((o) => o.label).join(' / '),
      bg: '#f1f2f3',
      border: '#e2e5e9',
      dot: '#AEB1B4',
      text: '#AEB1B4',
      hasArrow: false,
    })
  }

  return steps
}

/**
 * Which playbook template applies to a lead.
 *
 * Prospect/Lead splits on the consumer-Skida cross-check: a company where any
 * contact already has a consumer account gets the short, familiarity-assuming
 * variant; everyone else gets the longer educational one. Every other stage
 * maps straight to its own key.
 */
export function templateKeyFor(stageKey: string, knowsSkida: boolean): TemplateKey {
  if (stageKey === 'prospect') return knowsSkida ? 'prospect_aware' : 'prospect_unaware'
  return stageKey
}

/** Awareness badge shown next to the stage badge — Prospect/Lead only. */
export function awarenessBadge(
  stageKey: string,
  knowsSkida: boolean,
  pending: boolean,
): { label: string; color: string; bg: string } | null {
  if (stageKey !== 'prospect') return null
  if (pending) return { label: 'Checking Skida…', color: '#4c5b82', bg: '#f1f2f3' }
  return knowsSkida
    ? { label: 'Knows Skida', color: '#0b6e64', bg: '#e3f6f3' }
    : { label: 'New to Skida', color: '#4c5b82', bg: '#f1f2f3' }
}
